// Decisão pura de "aprova direto ou pede aprovação de gerente" — extraída de
// discountApproval.ts pra ficar testável (aquele arquivo importa supabase no
// topo, mesmo problema de todas as outras extrações desta leva: import.meta.env
// não existe fora do Vite). Esta é a peça que já mudou de comportamento duas
// vezes na mesma tarde (o bypass por role que escapava a Júlia da régua, e a
// carência que ela não deveria precisar de aprovação) — exatamente o tipo de
// lógica que merece teste automatizado, não só revisão visual.
import { corrigirPlanoPorPercentuais, getPlano, inferirPlanoPorPercentuais, modalidadePorPerfil } from './politicaComercial.ts';
import type { ModalidadePlano, PerfilParaModalidade } from './politicaComercial.ts';
import { isDentroPoliticaInterno } from './comissaoInterna.ts';
import { REGRA_BONIFICACAO } from './bonificacaoParceiro.ts';

export interface DecisaoAprovacaoDesconto {
  modalidade: ModalidadePlano;
  dentroDaPolitica: boolean;
  /** Sempre false pro Comercial Interno — carência não passa pela régua de
   *  aprovação nessa modalidade (decisão do dono do produto, 09/09/2026). */
  carenciaSolicitada: boolean;
  /** "Isento de fidelidade" escolhido agora — vale para todas as modalidades,
   *  inclusive o Comercial Interno (solicitação 6532, 30/10/2026). */
  fidelidadeIsentaSolicitada: boolean;
  /** dentroDaPolitica && !carenciaSolicitada && !fidelidadeIsentaSolicitada — true = aprova direto, sem nota nem notificação. */
  aprovadoDireto: boolean;
}

/**
 * Resolve SE uma negociação aprova direto ou entra na fila do gerente — não
 * toca banco, não envia notificação. `perfil` é de quem está salvando
 * (checkAndRequestDiscountApproval já resolveu o bypass de adm/gerente antes
 * de chegar aqui — esta função nunca vê esse caso).
 */
export function resolverAprovacaoDesconto(params: {
  perfil: PerfilParaModalidade;
  maxTarifa: number;
  maxBandeira: number;
  carencia?: string | null;
  planoCodigo?: string | null;
  /** true só quando quem salva acabou de escolher "Isento de fidelidade". Quem
   *  chama decide: vazio ou já isento antes não conta (as negociações antigas
   *  não entram na fila). */
  fidelidadeIsenta?: boolean;
}): DecisaoAprovacaoDesconto {
  const modalidade = modalidadePorPerfil(params.perfil);

  // Carência ≠ "sem carência" sempre exige aprovação — regra de jul/2026 do
  // vendedor externo. Comercial Interno fica de fora: "deve ser automático,
  // como acontecia antes" (achado ao ver a Júlia com uma solicitação pendente
  // só por causa da carência, quando o desconto em si estava dentro da política).
  const carenciaBrutaSolicitada = !!params.carencia && !/sem\s*car/i.test(params.carencia);
  const carenciaSolicitada = modalidade === 'vendedor_interno' ? false : carenciaBrutaSolicitada;

  let dentroDaPolitica: boolean;
  if (modalidade === 'vendedor_interno') {
    // Comercial Interno não tem plano_codigo — a régua é direto sobre os
    // percentuais: 20% a 30% de tarifa, bandeira 0/50/100.
    dentroDaPolitica = isDentroPoliticaInterno(params.maxTarifa, params.maxBandeira);
  } else if (modalidade === 'parceiro') {
    // Parceiro (regra da bonificação de 26/10/2026): o plano padrão é 20% de
    // tarifa; acima disso é condição especial e precisa de aprovação da Vigor —
    // mesmo quando os percentuais coincidem com um plano do vendedor externo
    // (22/50, 25/50...), que não vale para o parceiro. Bandeira: teto histórico de 50%.
    dentroDaPolitica = params.maxTarifa <= REGRA_BONIFICACAO.autoriza && params.maxBandeira <= 50;
  } else {
    // Resolve o plano da política: explícito (modal novo) ou inferido dos percentuais (fluxos legados)
    // "Fora da política" com percentuais de plano oficial é erro de
    // preenchimento, não exceção — corrige antes de decidir.
    const codigo = params.planoCodigo
      ? corrigirPlanoPorPercentuais(params.planoCodigo, params.maxTarifa, params.maxBandeira)
      : null;
    const plano = codigo
      ? getPlano(codigo)
      : inferirPlanoPorPercentuais(params.maxTarifa, params.maxBandeira);

    dentroDaPolitica = !!plano && !plano.foraDaPolitica;

    // Fluxos legados (UnidadeForm/Wizard) gravam percentuais livres — abaixo dos
    // tetos históricos (20% tarifa / 50% bandeira) segue aprovado como antes,
    // para não travar cadastros feitos antes da escolha do plano.
    if (!params.planoCodigo && !dentroDaPolitica) {
      dentroDaPolitica = params.maxTarifa <= 20 && params.maxBandeira <= 50;
    }
  }

  const fidelidadeIsentaSolicitada = !!params.fidelidadeIsenta;

  return {
    modalidade,
    dentroDaPolitica,
    carenciaSolicitada,
    fidelidadeIsentaSolicitada,
    aprovadoDireto: dentroDaPolitica && !carenciaSolicitada && !fidelidadeIsentaSolicitada,
  };
}
